import { createFileRoute } from "@tanstack/react-router";
import { TableSkeleton } from "@/components/shared/Skeletons";
import { CircleCheck, ClipboardList, Hourglass, ListChecks, Play } from "lucide-react";
import { useSimulatedTasks, useSimulatedStats, SimulatedTask } from "@/hooks/useSimulated";
import StatCard from "@/components/shared/StatCard";
import PageHeader from "@/components/shared/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Link } from "@tanstack/react-router";

export const Route = createFileRoute("/student/simulated/")({
  component: SimulatedTasks,
});

function SimulatedTasks() {
  const { data: tasks, isLoading } = useSimulatedTasks() as { data: SimulatedTask[] | undefined; isLoading: boolean };
  const stats = useSimulatedStats();
  const tasksList = Array.isArray(tasks) ? tasks : [];
  const pendingTasks = stats.totalTasks - stats.completedTasks;

  if (isLoading) {
    return <TableSkeleton rows={5} columns={4} />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Simulated work experience"
        subtitle="Build practical skills and work logs before your real placement"
      />

      <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
        <StatCard label="Total hours" value={stats.totalHours.toFixed(1)} icon={<ClipboardList />} />
        <StatCard label="Completed" value={stats.completedTasks} icon={<ListChecks />} />
        <StatCard label="Pending" value={pendingTasks} icon={<Hourglass />} />
        <StatCard label="Status" value={"Trainee"} icon={<CircleCheck />} />
      </div>

      <section aria-labelledby="sim-tasks-heading">
        <h2 id="sim-tasks-heading" className="mb-3 text-title text-foreground">
          Available tasks
        </h2>
        <ul className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {tasksList.map((task) => (
            <li
              key={task.id}
              className="flex h-full flex-col rounded-md border border-border bg-card p-4 md:p-5"
            >
              <div className="mb-3 flex items-center justify-between gap-2">
                <Badge
                  className="capitalize"
                  dot
                  variant={
                    task.difficulty === "beginner" ? "success" :
                    task.difficulty === "intermediate" ? "warning" : "destructive"
                  }
                >
                  {task.difficulty}
                </Badge>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline" className="tabular-nums" tabIndex={0}>
                      {`${task.estimated_hours}h`}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>Estimated hours</TooltipContent>
                </Tooltip>
              </div>
              <h3 className="mb-1 text-[15px] font-medium text-foreground">{task.title}</h3>
              <p className="mb-4 text-sm text-muted-foreground">{task.description}</p>
              <Button asChild variant="outline" className="mt-auto w-full">
                <Link to={`/student/simulated/${task.id}` as any}>
                  <Play />
                  Start task
                </Link>
              </Button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
